import { useEffect, useRef } from 'react'
import type { HubConnection } from '@microsoft/signalr'
import { connectMessagesHub, type MessageReceivedPayload } from '@/api/common/MessagesHub'
import { tokenStore } from '@/api/tokenStore'

/**
 * Subscribes to the `OnMessageReceived` event pushed by CrmService's CrmChatHub (via
 * `/hubs/chat`). Connects on mount, disconnects on unmount — same proven direct-SignalR
 * lifecycle as `useInventoryAlerts` (shared-core), built on `connectMessagesHub` rather
 * than `ChatHubService`/`SignalRServiceBase` (see MessagesHub.ts's doc comment for why
 * that base class is untested boilerplate best avoided for new work).
 *
 * Lives in admin-dashboard's own hooks folder (not shared-core) because this app's
 * `@/hooks/*` alias is hard-pointed at shared-core/src/hooks — a file placed here is
 * only reachable via a relative import, which is exactly how route.tsx wires it in.
 */
export function useMessagesRealtime(onMessage?: (payload: MessageReceivedPayload) => void) {
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage

  const connectionRef = useRef<HubConnection | null>(null)

  useEffect(() => {
    const token = tokenStore.get()?.accessToken
    if (!token) return

    const connection = connectMessagesHub(token, (payload) => onMessageRef.current?.(payload))
    connectionRef.current = connection

    return () => {
      connection.stop().catch(() => {
        /* best-effort — the tab is navigating away or unmounting either way */
      })
      connectionRef.current = null
    }
  }, [])
}
